import React, { useState, useEffect } from 'react';
import { FaDownload, FaHistory, FaLightbulb, FaChevronRight } from 'react-icons/fa';
import './Sidebar.css';

const Sidebar = ({ 
  chatHistory = [], 
  onExampleClick, 
  onHistoryClick,
  onDownloadChat 
}) => {
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [examplePrompts, setExamplePrompts] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [loading, setLoading] = useState(true);

  // Load data from backend on component mount
  useEffect(() => {
    loadDataFromBackend();
  }, []);

  // Track user questions from chat history and save to backend
  useEffect(() => {
    const userQuestions = chatHistory
      .filter(chat => chat.type === 'user')
      .map(chat => chat.message)
      .slice(-10); // Get last 10 questions
    
    setRecentQuestions(userQuestions);
    
    // Save recent questions to backend
    if (userQuestions.length > 0) {
      saveRecentMessagesToBackend(userQuestions);
    }
  }, [chatHistory]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000`;

  // Load static examples and clear recent messages from backend
  const loadDataFromBackend = async () => {
    try {
      setLoading(true);
      
      // Load static examples
      const examplesResponse = await fetch(`${backendBase}/api/sidebar/static-examples`);
      if (examplesResponse.ok) {
        const examplesData = await examplesResponse.json();
        if (examplesData.success) {
          setExamplePrompts(examplesData.examples);
        }
      }
      
      // Clear recent messages on startup
      setRecentQuestions([]);
      await fetch(`${backendBase}/api/sidebar/recent-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: [] })
      });
    } catch (error) {
      console.error('Error loading data from backend:', error);
      // Fallback to default examples
      setExamplePrompts([
        "Generate a demographics table for the study population",
        "Create a Kaplan-Meier survival plot for time to event",
        "Show me the adverse events summary table",
        "Generate efficacy analysis for primary endpoint",
        "Create a safety summary with treatment-emergent adverse events"
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Save recent messages to backend
  const saveRecentMessagesToBackend = async (messages) => {
    try {
      await fetch(`${backendBase}/api/sidebar/recent-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages }),
      });
    } catch (error) {
      console.error('Error saving recent messages to backend:', error);
    }
  };

  // Save static examples to backend
  const saveStaticExamplesToBackend = async (examples) => {
    try {
      await fetch(`${backendBase}/api/sidebar/static-examples`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ examples }),
      });
    } catch (error) {
      console.error('Error saving static examples to backend:', error);
    }
  };

  const handleExampleClick = (prompt) => {
    if (onExampleClick) {
      onExampleClick(prompt);
    }
  };

  const handleHistoryClick = (question) => {
    if (onHistoryClick) {
      onHistoryClick(question);
    }
  };

  const handleDownloadChat = () => {
    if (onDownloadChat) {
      onDownloadChat();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>

      {!isCollapsed && (
        <div className="sidebar-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading sidebar data...</p>
            </div>
          ) : (
            <>
              {/* Static Examples Section */}
              <div className="sidebar-section">
                <div className="section-header">
                  <FaLightbulb className="section-icon" />
                  <h3>Example Prompts</h3>
                </div>
                <div className="prompts-list">
                  {examplePrompts.map((prompt, index) => (
                    <button
                      key={index}
                      className="prompt-item"
                      onClick={() => handleExampleClick(prompt)}
                      title={prompt}
                    >
                      <span className="prompt-text">{prompt}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Recent Questions Section */}
              <div className="sidebar-section">
                <div className="section-header">
                  <FaHistory className="section-icon" />
                  <h3>Recent Questions</h3>
                </div>
                <div className="history-list">
                  {recentQuestions.length > 0 ? (
                    recentQuestions.map((question, index) => (
                      <button
                        key={index}
                        className="history-item"
                        onClick={() => handleHistoryClick(question)}
                        title={question}
                      >
                        <span className="question-text">{question}</span>
                        <span className="question-time">
                          {recentQuestions.length - index} questions ago
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="empty-state">
                      <p>No recent questions yet</p>
                      <small>Start chatting to see your question history</small>
                    </div>
                  )}
                </div>
              </div>

              {/* Download Chat Section */}
              <div className="sidebar-section download-section">
                <button 
                  className="download-button"
                  onClick={handleDownloadChat}
                  title="Download entire chat as HTML file"
                >
                  <FaDownload className="download-icon" />
                  <span>Download Chat (HTML)</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
      </div>
      
      {/* Toggle Button - Always visible */}
      <button 
        className="sidebar-toggle"
        onClick={toggleCollapse}
        title={isCollapsed ? "Open sidebar" : "Close sidebar"}
      >
        <FaChevronRight className={`chevron ${isCollapsed ? 'rotated' : ''}`} />
      </button>
    </>
  );
};

export default Sidebar;
